import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { log } from "../lib/log";
import { W, H, project } from "./worldmap/constants";
import HubPulses from "./worldmap/HubPulses";
import { useWorldPaths } from "./worldmap/useWorldPaths";
import { useMapView } from "./worldmap/useMapView";
import { useVisibleLines } from "./worldmap/useVisibleLines";
import { useLabelCulling } from "./worldmap/useLabelCulling";
import { layoutAstroLines, clusterPrevPins } from "./worldmap/layout";
import { MapDefs, MapBackground, AxisLabels } from "./worldmap/MapChrome";
import { CountryLayer, ZoomLabels, CountryTooltip } from "./worldmap/CountryLayer";
import { AstroLinesLayer, PrevPinsLayer, ActivePin } from "./worldmap/PinLayers";
import { LineLegend, MapHeader } from "./worldmap/MapControls";

const scoreColorFor = (s) => (s >= 90 ? "#C8A96E" : s >= 75 ? "#9CBF8E" : s >= 55 ? "#ffffff" : "#A89C98");
const glowFor = (s) => (s >= 90 ? "#C8A96E" : s >= 75 ? "#9CBF8E" : "#4A3356");

export default function WorldMap({ lat, lng, city, score, easterEgg, previousPins = [], astroLines = [] }) {
  const nav = useNavigate();
  const svgRef = useRef(null);
  const [cx, cy] = useMemo(() => project(lat, lng, W, H), [lat, lng]);
  const [activeHubs, setActiveHubs] = useState([]);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [visibleLines, toggleLine] = useVisibleLines();
  const worldPaths = useWorldPaths();
  const { view, zoom, resetView, flyTo, toViewCoords, wasDragged, svgHandlers } = useMapView(svgRef);
  const { zoomK, culledCountryLabels, culledCityLabels } = useLabelCulling(worldPaths, view);

  useEffect(() => {
    api.get("/hubs")
      .then((res) => setActiveHubs(res.data || []))
      .catch((e) => log.warn("WorldMap: hubs fetch failed:", e));
  }, []);

  // Glide to the scanned city (3× zoom) once the countries are drawn.
  const hasPaths = worldPaths.length > 0;
  useEffect(() => {
    if (!hasPaths) return;
    const t = setTimeout(() => flyTo(cx, cy, 3), 350);
    return () => clearTimeout(t);
  }, [cx, cy, hasPaths, flyTo]);

  const stackedLines = useMemo(() => layoutAstroLines(astroLines, visibleLines, cx, cy), [astroLines, visibleLines, cx, cy]);
  const clusteredPrevPins = useMemo(() => clusterPrevPins(previousPins), [previousPins]);

  const openHub = (cityName) => {
    if (wasDragged()) return;
    nav(`/chat/hub/${encodeURIComponent(cityName.toLowerCase())}`);
  };

  return (
    <div className="glass rounded-2xl p-3 sm:p-6 relative overflow-hidden" data-testid="scanner-worldmap">
      <MapHeader city={city} lat={lat} lng={lng} score={score} scoreColor={scoreColorFor(score)} onZoom={zoom} onReset={resetView} />

      <div className="relative w-full select-none aspect-[4/5] sm:aspect-[900/460] rounded-lg overflow-hidden touch-none">
        <svg
          ref={svgRef}
          viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
          className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing"
          preserveAspectRatio="xMidYMid slice"
          {...svgHandlers}
        >
          <MapDefs glow={glowFor(score)} />
          <MapBackground />
          <CountryLayer worldPaths={worldPaths} view={view} toViewCoords={toViewCoords} setHoverInfo={setHoverInfo} />
          <ZoomLabels zoomK={zoomK} view={view} culledCountryLabels={culledCountryLabels} culledCityLabels={culledCityLabels} />
          <AxisLabels />
          <AstroLinesLayer lines={stackedLines} />
          <PrevPinsLayer clusters={clusteredPrevPins} onOpenHub={openHub} />
          <ActivePin cx={cx} cy={cy} city={city} glow={glowFor(score)} easterEgg={easterEgg} onOpenHub={openHub} />
          <HubPulses hubs={activeHubs} viewX={view.x} viewY={view.y} viewW={view.w} viewH={view.h} />
          <style>{`@keyframes dash { to { stroke-dashoffset: 0; } }`}</style>
        </svg>
        <CountryTooltip hoverInfo={hoverInfo} view={view} />
      </div>

      <LineLegend visibleLines={visibleLines} onToggle={toggleLine} />
    </div>
  );
}
